import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { parsePln, plnToInput } from '../catalog/money';
import { todayInWarsaw } from '../events/calendar';
import { trapTab } from '../ui/focus';
import { formText, METHODS, type Method, type Receivable } from './types';
import { DialogLayer } from '../ui/Dialog';
import { NotesInput } from '../ui/NotesInput';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

/**
 * "Odnotuj wpłatę" (F36): a payment without a bank statement — cash, a transfer or barter — booked against one
 * receivable. The amount starts as what is left to pay; more stays as the client's credit. "Potwierdź płatność"
 * (owner 2026-10-22, "Do potwierdzenia") is the same dialog under its own title.
 */
export function RecordPaymentDialog({ receivable, onClose, onSaved, title = 'Odnotuj wpłatę', featureId = 'payments.record' }: { receivable: Receivable; onClose: () => void; onSaved: () => void; title?: string; featureId?: string }) {
  const [amount, setAmount] = useState(receivable.remainingGrosze !== undefined ? plnToInput(receivable.remainingGrosze) : '');
  const [paidOn, setPaidOn] = useState(todayInWarsaw());
  const [method, setMethod] = useState<Method>(receivable.method);
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');
  const opening = useOpening({ amount, paidOn, method, note });
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/payments/receivables/${receivable.id}/allocations`, { method: 'POST', body }),
    onSuccess: onSaved,
  });
  /** What to send, or null with the reason shown. */
  function payload() {
    const fail = (why: string) => (setLocal(why), null);
    let amountGrosze: number;
    try {
      amountGrosze = parsePln(amount);
    } catch (error) {
      return fail(error instanceof Error ? error.message : 'Nieprawidłowa kwota');
    }
    if (amountGrosze <= 0) return fail('Kwota musi być większa od zera');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paidOn)) return fail('Podaj datę wpłaty');
    setLocal('');
    return { amountGrosze, method, paidOn, ...(note.trim() ? { note: note.trim() } : {}) };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const sent = payload();
    if (sent !== null) save.mutate(sent);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ amount, paidOn, method, note }, opening),
    save: async () => {
      const sent = payload();
      if (sent === null) return false;
      await save.mutateAsync(sent);
      return true;
    },
  });
  const error = save.error instanceof ApiError ? (fieldIssues(save.error)[0]?.message ?? save.error.message) : save.error ? 'Nie udało się zapisać' : '';
  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-payment-title"
        data-feature-id={featureId}
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="record-payment-title">{title}</h2>
        <p className="muted">
          {receivable.label}
          {receivable.contractorName ? ` · ${receivable.contractorName}` : ''}
          {receivable.form ? ` · ${formText(receivable)}` : ''}
        </p>
        <label className="field">
          <span>Kwota (zł)</span>
          <input
            autoFocus
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => {
              try {
                setAmount(plnToInput(parsePln(amount)));
              } catch {
                /* the message comes on saving */
              }
            }}
          />
        </label>
        <fieldset className="field">
          <legend>Sposób</legend>
          <div className="segment">
            {(Object.keys(METHODS) as Method[]).map((m) => (
              <label key={m} className={method === m ? 'on' : undefined}>
                <input type="radio" name="method" checked={method === m} onChange={() => setMethod(m)} /> {METHODS[m]}
              </label>
            ))}
          </div>
        </fieldset>
        {method === 'barter' && <p className="muted small">Wpłata barterem zapisze wpis „dostaliśmy” u tego kontrahenta w „Finanse → Barter”.</p>}
        <label className="field">
          <span>Data wpłaty</span>
          <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </label>
        <label className="field">
          <span>Uwagi</span>
          <NotesInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </label>
        {(local || error) && (
          <p className="alert" role="alert">
            {local || error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
